import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Info } from "lucide-react";
import { getCommandCenter, getHomeHandoff, getEmployerLink, handoffUrl } from "@/lib/api";
import { Banner } from "@/components/ui-bits";
import { toast } from "sonner";
import { Copy, QrCode } from "lucide-react";

function HandoffCard() {
  const [s, setS] = useState(null);
  const [link, setLink] = useState(null);
  useEffect(() => { getHomeHandoff().then((d) => setS(d.session)).catch(() => {}); getEmployerLink().then(setLink).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <div className="panel handoff-signal" data-testid="home-handoff-card" style={{ marginBottom: 20 }}>
      <div className="ph"><h2>Today's Interview Handoff</h2><span className="pill waiting" style={{ marginLeft: 8 }}>Live</span><span className="sp" /><span className="mut hs-code">Session {s.sessionCode}</span></div>
      <div className="pb">
        <div className="hs-row">
          <div><div className="hs-emp">{s.employerName}</div><div className="mut">{s.interviewDateLabel}</div></div>
          <div className="hs-nums">
            <div><b>{s.expected}</b><span>Expected</span></div>
            <div><b>{s.completed}</b><span>Completed</span></div>
            <div><b>{s.hire}</b><span>Hire Approved</span></div>
          </div>
        </div>
        <div className="action-row" style={{ marginTop: 14 }}>
          <a className="btn primary" href="/interview-sessions" data-testid="home-open-session">Open Session</a>
          <button className="btn" onClick={() => { navigator.clipboard?.writeText(handoffUrl(link?.token || s.token)); toast.success("Employer link copied"); }} data-testid="home-copy-link"><Copy size={16} /> Copy Employer Link</button>
        </div>
      </div>
    </div>
  );
}

function Tile({ tile }) {
  return (
    <Link className={`tile t-${tile.tone}${tile.key === "interview_waiting" ? " t-focus" : ""}`} to={tile.to} data-testid={`tile-${tile.key}`}>
      <div className="lb">{tile.label}</div>
      <div className="vl">{tile.value}</div>
      <div className="sb">
        <span className="go">{tile.sub}</span> <ArrowRight size={13} />
      </div>
    </Link>
  );
}

const ACTION_PILL = {
  reviewflag: "reviewflag",
  crit: "crit",
  awaiting: "awaiting",
  approved: "approved",
};

export default function CommandCenter() {
  const [data, setData] = useState(null);

  useEffect(() => {
    getCommandCenter().then(setData).catch(() => {});
  }, []);

  if (!data) return <div className="empty" data-testid="cc-loading">Loading Staffing Home…</div>;

  return (
    <div data-testid="command-center">
      <HandoffCard />
      <Banner variant="neutral" icon={<Clock size={18} />}>
        <strong>{data.banner.dateLabel}.</strong> {data.banner.text}
      </Banner>

      <div className="tile-group-label">
        Candidate Pipeline
        <span className="hint">— select any number to open those records</span>
      </div>
      <div className="tiles cols-3" data-testid="pipeline-tiles">
        {data.pipeline.map((t) => (
          <Tile key={t.key} tile={t} />
        ))}
      </div>

      <div className="note-compact" data-testid="hire-approved-note">
        <Info size={15} />
        <span>
          <strong>"Hire Approved" is not "hired".</strong> The employer approved a hire; MIHP must still
          communicate with the candidate and run onboarding. There is <strong>no direct step</strong> from
          Hire Approved to Converted to Employee.
        </span>
      </div>

      <div className="tile-group-label">
        Active Candidate Pool
        <span className="hint">— the pool duration is a configurable setting, currently {data.poolDays} days</span>
      </div>
      <div className="tiles cols-4" data-testid="pool-health-tiles">
        {data.poolHealth.map((t) => (
          <Tile key={t.key} tile={t} />
        ))}
      </div>

      <div className="tile-group-label">
        Action Center <span className="hint">— work surfaced, not searched for</span>
      </div>
      <div className="panel list-panel" data-testid="action-center">
        <div className="ph">
          <h2>Needs your attention</h2>
          <span className="sp" />
          <button className="ph-link">Dismiss reviewed</button>
        </div>
        <div className="pb flush">
          <table>
            <thead>
              <tr>
                <th>What</th>
                <th>Who</th>
                <th>Why it is here</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.actionItems.map((it, i) => (
                <tr key={i} data-testid={`action-item-${i}`}>
                  <td data-label="What"><span className={`pill ${ACTION_PILL[it.kind] || ""}`}>{it.pill}</span></td>
                  <td data-label="Who">
                    <span className="nm">{it.name}</span>
                    <div className="mut">{it.ref}</div>
                  </td>
                  <td className="why" data-label="Why">{it.why}</td>
                  <td data-label="">
                    <Link
                      className={`btn sm${it.kind === "approved" ? " primary" : ""}`}
                      to={it.to}
                      data-testid={`action-btn-${i}`}
                    >
                      {it.actionLabel}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
